<template>
  <header-block name="Editor" :page="activePage.name">
    <div class="col">
      <nav class="btn-links mx-auto">
        <button :class="{ active: size === 'wide' }" v-on:click="size = 'wide'">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M4 5v11h16V5H4zm-2-.993C2 3.451 2.455 3 2.992 3h18.016c.548 0 .992.449.992 1.007V18H2V4.007zM1 19h22v2H1v-2z"
            />
          </svg>
        </button>
        <button
          :class="{ active: size === 'tablet' }"
          v-on:click="size = 'tablet'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="icon icon-tabler icons-tabler-outline icon-tabler-device-ipad-horizontal"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path
              d="M3 6a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-12z"
            />
            <path d="M9 17h6" />
          </svg>
        </button>
        <button
          :class="{ active: size === 'phone' }"
          v-on:click="size = 'phone'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M7 4v16h10V4H7zM6 2h12a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm6 15a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
            />
          </svg>
        </button>
      </nav>
    </div>
    <div class="col-4 text-end">
      <select class="form-control lang-selector" v-model="language">
        <option v-for="lang in languages" :key="lang.code" :value="lang.code">
          {{ lang.name }}
        </option>
      </select>
      <nav>
        <button v-on:click="save()">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M18 19h1V6.828L17.172 5H16v4H7V5H5v14h1v-7h12v7zM4 3h14l2.707 2.707a1 1 0 0 1 .293.707V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm4 11v5h8v-5H8z"
            />
          </svg>
        </button>
      </nav>
    </div>
  </header-block>
  <div class="container-fluid mt-3">
    <div class="row">
      <div class="col-2 max-height">
        <div class="white-box">
          <div class="row align-items-center">
            <div class="col">
              <h1>Blocks</h1>
            </div>
            <div class="col-auto">
              <button v-on:click="addBlockShow = true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M11 11V7h2v4h4v2h-4v4h-2v-4H7v-2h4zm1 11C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <draggable
            class="drag-area blocks"
            tag="ul"
            :list="blocks"
            :group="{ name: 'g1' }"
            item-key="name"
            v-on:end="moveEvent"
            v-bind="dragOptions"
          >
            <template #item="{ element }">
              <li
                class="align-items-center row"
                v-on:click="showEditBlock(element)"
              >
                <span v-html="element?.type?.icon" class="col-auto"></span>
                <span class="col">{{ element?.value?.block_name ?? (element?.type?.name ?? element.key) }}</span>
              </li>
            </template>
          </draggable>
        </div>
      </div>
      <div class="col-7 preview-container" ref="outsideContainer">
        <div
          ref="container"
          class="iframe-container"
          :class="[size]"
          :style="`transform: scale(${rate});`"
        >
          <iframe
            ref="preview"
            :style="[
              height ? 'height: ' + height + 'px' : '',
              zoom ? 'zoom: ' + zoom : '',
            ]"
          ></iframe>
        </div>
        <div
          class="preview-loading"
          :class="{ show: !loaded.style || !loaded.script || !loaded.page }"
        >
          <div
            ref="loadBar"
            data-preset="bubble"
            class="ldBar label-center"
          ></div>
        </div>
      </div>
      <div class="col-3 max-height" v-if="selectedBlock">
        <section class="white-box">
          <div class="row align-items-center">
            <div class="col-auto" v-html="selectedBlock?.type?.icon"></div>
            <div class="col">
              <h1>Module</h1>
              <div class="row">
                <div class="col">
                  <h2 v-on:click="editBlockName = true" class="editable-text" v-if="!editBlockName">{{ selectedBlock?.value?.block_name ?? selectedBlock?.type?.name }}</h2>
                  <input
                      type="text"
                      class="form-control"
                      v-model="selectedBlock.value.block_name"
                      v-else
                      @blur="editBlockName = false"
                      @keyup.enter="editBlockName = false"
                  />
                </div>
                <div class="col-auto">
                  <button
                    v-if="
                      selectedBlock?.type?.blocks?.some(
                        (block) => block.type !== 'array'
                      )
                    "
                    v-on:click="
                      editContent(
                        selectedBlock.type.blocks,
                        selectedBlock.value.settings
                      )
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="icon icon-tabler icons-tabler-outline icon-tabler-pencil-discount"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path
                        d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"
                      />
                      <path d="M13.5 6.5l4 4" />
                      <path d="M16 21l5 -5" />
                      <path d="M21 21v.01" />
                      <path d="M16 16v.01" />
                    </svg>
                  </button>
                  <button v-on:click="deleteBlock">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                    >
                      <path
                        d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div class="white-box">
          <div class="row mb-3">
            <div class="col-12 mb-2">
              <h1>Visibility</h1>
            </div>
            <ConditionGroupEditor
              :variables="activePossibleVariables"
              v-model="this.selectedBlock.value.conditions"
            />
          </div>
        </div>
        <div class="white-box styles-block">
          <div class="row mb-3">
            <div class="col-12 mb-2">
              <h1>Style</h1>
            </div>
          </div>
          <div
            class="row"
            v-for="block in selectedBlock?.type?.blocks.filter(
              (e) => e.styles?.length > 0
            )"
            :key="block.key"
          >
            <div class="col-12">
              <h3>{{ block.name }}</h3>
            </div>
            <template v-for="style in block.styles" :key="style">
              <div class="col-12 mb-2">
                <div class="input-group" v-if="style.classes">
                  <label class="half">{{ style.name }}</label>
                  <select
                    v-model="selectedBlock.value.settings[style.key]"
                    v-on:change="changeBlockStyle()"
                  >
                    <option v-for="(c, i) in style.classes" :key="i" :value="i">
                      {{ c }}
                    </option>
                  </select>
                </div>
                <div class="input-group" v-else>
                  <input
                    type="checkbox"
                    :checked="
                      selectedBlock.value.settings[style.key] === style.class
                    "
                    :value="style.class"
                    v-on:change="toggleStyle(style)"
                  />
                  <label>{{ style.name }}</label>
                </div>
              </div>
            </template>
          </div>
          <div class="row">
            <div class="col-12 mb-2">
              <h3>Margin</h3>
            </div>
            <div class="col-6">
              <div class="input-group">
                <label>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 2v2H6V7h12z"
                    />
                  </svg>
                </label>
                <select
                  v-model="values['top']"
                  v-on:change="changeBlockStyle()"
                >
                  <option
                    v-for="(name, key) in margin.top.classes"
                    :key="key"
                    :value="key"
                  >
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-6">
              <div class="input-group">
                <label>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 10v2H6v-2h12z"
                    />
                  </svg>
                </label>
                <select
                  v-model="values['bottom']"
                  v-on:change="changeBlockStyle()"
                >
                  <option
                    v-for="(name, key) in margin.bottom.classes"
                    :key="key"
                    :value="key"
                  >
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-6">
              <div class="input-group">
                <label>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 2v10h-2V7h2z"
                    />
                  </svg>
                </label>
                <select
                  v-model="values['left']"
                  v-on:change="changeBlockStyle()"
                >
                  <option
                    v-for="(name, key) in margin.left.classes"
                    :key="key"
                    :value="key"
                  >
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-6">
              <div class="input-group">
                <label>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zM8 7v10H6V7h2z"
                    />
                  </svg>
                </label>
                <select
                  v-model="values['right']"
                  v-on:change="changeBlockStyle()"
                >
                  <option
                    v-for="(name, key) in margin.right.classes"
                    :key="key"
                    :value="key"
                  >
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div
          class="white-box"
          v-for="block in selectedBlock?.type?.blocks?.filter(
            (block) => block.type === 'array'
          )"
          :key="block.key"
        >
          <div class="row mb-3">
            <div class="col mb-2">
              <h1>{{ block.name }}</h1>
            </div>
            <div class="col-auto">
              <button v-on:click="addNewBlockItem(block)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="icon icon-tabler icons-tabler-filled icon-tabler-library-plus"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path
                    d="M18.333 2a3.667 3.667 0 0 1 3.667 3.667v8.666a3.667 3.667 0 0 1 -3.667 3.667h-8.666a3.667 3.667 0 0 1 -3.667 -3.667v-8.666a3.667 3.667 0 0 1 3.667 -3.667zm-4.333 4a1 1 0 0 0 -1 1v2h-2a1 1 0 0 0 0 2h2v2a1 1 0 0 0 2 0v-2h2a1 1 0 0 0 0 -2h-2v-2a1 1 0 0 0 -1 -1"
                  />
                  <path
                    d="M3.517 6.391a1 1 0 0 1 .99 1.738c-.313 .178 -.506 .51 -.507 .868v10c0 .548 .452 1 1 1h10c.284 0 .405 -.088 .626 -.486a1 1 0 0 1 1.748 .972c-.546 .98 -1.28 1.514 -2.374 1.514h-10c-1.652 0 -3 -1.348 -3 -3v-10.002a3 3 0 0 1 1.517 -2.605"
                  />
                </svg>
              </button>
            </div>
          </div>
          <template v-if="selectedBlock.value.settings[block.key]">
            <draggable
                tag="ul"
                class="drag-area"
                :list="selectedBlock.value.settings[block.key]"
                :group="{ name: 'cms-blocks' }"
                item-key="name"
                v-bind="dragOptions"
            >
              <template #item="{ element, index }">
                <li class="row align-items-center flex-column nested-group">

                  <!-- Tob block-->
                  <div class="d-flex w-100 align-items-center">
                    <div class="col">
                      <h2>{{ index + 1 }}</h2>
                      <p>{{ getSectionTitle(element, block) }}</p>
                    </div>

                    <div class="col-auto">
                      <button @click="editContent(block.blocks, element)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-pencil-cog"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" /><path d="M13.5 6.5l4 4" /><path d="M19.001 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M19.001 15.5v1.5" /><path d="M19.001 21v1.5" /><path d="M22.032 17.25l-1.299 .75" /><path d="M17.27 20l-1.3 .75" /><path d="M15.97 17.25l1.3 .75" /><path d="M20.733 20l1.3 .75" /></svg>
                      </button>
                      <button @click="deleteSubBlock(block.key, index)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-trash-x"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7h16" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /><path d="M10 12l4 4m0 -4l-4 4" /></svg>
                      </button>
                    </div>
                  </div>

                  <!-- ✅ Nested block -->
                  <div v-if="block.blocks && block.blocks.findIndex((e) => e.type === 'array') > -1" class="nested-subgroup">
                    <template v-for="subBlock in block.blocks" :key="subBlock.key">
                      <template v-if="subBlock.type === 'array'">
                        <div class="row">
                          <div class="col">
                            <h4 class="group-header">{{ subBlock.name }}</h4>
                          </div>
                          <div class="col-auto">
                            <button @click="addNestedItem(block.key, index, subBlock)">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-copy-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path stroke="none" d="M0 0h24v24H0z" /><path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2 2 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /><path d="M11 14h6" /><path d="M14 11v6" /></svg>
                            </button>
                          </div>
                        </div>
                        <draggable
                            tag="ul"
                            class="nested-drag"
                            :list="element[subBlock.key]"
                            :group="{ name: 'cms-blocks' }"
                            item-key="name"
                            v-bind="dragOptions"
                        >
                          <template #item="{ element: child, index: i }">
                            <li class="d-flex justify-content-between">
                              <span>{{ i + 1 }} – {{ getSectionTitle(child, subBlock) }}</span>
                              <div>
                                <button @click="editContent(subBlock.blocks, child)">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-pencil-cog"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" /><path d="M13.5 6.5l4 4" /><path d="M19.001 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M19.001 15.5v1.5" /><path d="M19.001 21v1.5" /><path d="M22.032 17.25l-1.299 .75" /><path d="M17.27 20l-1.3 .75" /><path d="M15.97 17.25l1.3 .75" /><path d="M20.733 20l1.3 .75" /></svg>
                                </button>
                                <button @click="deleteNestedItem(block.key, index, subBlock.key, i)">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-trash-x"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7h16" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /><path d="M10 12l4 4m0 -4l-4 4" /></svg>
                                </button>
                              </div>
                            </li>
                          </template>
                        </draggable>
                      </template>
                    </template>
                  </div>

                </li>
              </template>
            </draggable>
          </template>
        </div>
      </div>
    </div>
  </div>
  <AddBlock v-model:show="addBlockShow" v-on:addBlock="addBlock"  v-bind:template="template" />
  <EditContent
    v-model:show="editContentShow"
    v-model:block="selectedBlock"
    v-model:elements="editContentBlock"
    v-model:settings="editContentSettings"
    :variables="activePossibleVariables"
    :dynamic="activeDynamicBlocks"
    :languages="languages"
    v-on:save="editContentChange"
  />
  <GlobalSettings
    v-model:show="globalShettingsShow"
    v-model="globalVariables"
    v-on:change="changeStyle"
    v-on:importCss="addImportCss"
  />
</template>

<script>
import Twig from "twig";

import draggable from "vuedraggable";
import AddBlock from "../components/modals/AddBlock";
import GlobalSettings from "../components/modals/GlobalSettings";
import "../loading-bar";
import HeaderBlock from "../components/header";
import http from "../modules/http";
import EditContent from "../components/modals/EditContent";
import ConditionGroupEditor from "../components/ConditionGroupEditor";

export default {
  name: "BlockEditor",
  components: {
    EditContent,
    HeaderBlock,
    GlobalSettings,
    AddBlock,
    draggable,
    ConditionGroupEditor,
  },
  watch: {
    language(value, old) {
      if (
        value !== null &&
        old !== null &&
        value !== undefined &&
        old !== undefined
      ) {
        (async () => {
          await this.loadBlocks();
          this.loadJson();
        })();
      }
    },
    size() {
      setTimeout(() => {
        this.calcRate();
      }, 1);
    },
  },
  data() {
    return {
      template: undefined,
      size: "wide",
      rate: 1,
      language: null,
      activePage: {
        id: this.$route.params.id,
        language: "",
      },
      languages: [],
      zoom: undefined,
      height: undefined,
      values: {},
      content: {},
      pageJson: null,
      contentForLayout: null,
      generatedCss: null,
      generatedCssLink: null,
      loaded: {
        script: false,
        style: false,
        page: false,
        content: false,
        var: 0,
        bar: undefined,
      },
      addBlockShow: false,
      globalShettingsShow: false,
      editContentShow: false,
      editContentBlock: null,
      editContentSettings: null,
      editName: "",
      editI: 0,
      globalVariables: [],
      editBlockName: false,
      selectedBlock: undefined,
      margin: {
        top: {
          classes: {
            "": "default",
            "mt-auto": "auto",
            "mt-0": "0x",
            "mt-1": "1x",
            "mt-2": "2x",
            "mt-3": "3x",
            "mt-4": "4x",
            "mt-5": "5x",
          },
        },
        bottom: {
          classes: {
            "": "default",
            "mb-auto": "auto",
            "mb-0": "0x",
            "mb-1": "1x",
            "mb-2": "2x",
            "mb-3": "3x",
            "mb-4": "4x",
            "mb-5": "5x",
          },
        },
        left: {
          classes: {
            "": "default",
            "ml-auto": "auto",
            "ml-0": "0x",
            "ml-1": "1x",
            "ml-2": "2x",
            "ml-3": "3x",
            "ml-4": "4x",
            "ml-5": "5x",
          },
        },
        right: {
          classes: {
            "": "default",
            "mr-auto": "auto",
            "mr-0": "0x",
            "mr-1": "1x",
            "mr-2": "2x",
            "mr-3": "3x",
            "mr-4": "4x",
            "mr-5": "5x",
          },
        },
      },
      blocks: [],
      files: {},
      jsonUnderLoad: false,
      possibleBlocks: [],
      interval: null,
      possibleVariables: {
        1: ["category.id"], //list
        2: [
          "product.category_id",
          "product.serial",
          "product.sku",
          "product.ean",
          "product.upc",
          "product.mpn",
          "product.id",
          "product.product_id",
          "product.stock",
          "product.avg_monthly_orders",
          "product.preorder",
          "product.price.amount",
          "product.price.currency",
          "product.discount",
          "product.schemas.brand.id",
          "product.schemas.author.id",
          "product.schemas.color.id",
          "product.schemas.manufacturer.id",
          "product.schemas.material.id",
          "product.schemas.model.id",
          "product.schemas.release_date.id",
          "product.schemas.size.id",
          "product.schemas.pattern.id",
          "product.schemas.is_adult.id",
          "product.schemas.bundle.id",
          "product.schemas.energy_efficiency_class.id",
          "product.schemas.age_group.id",
          "product.schemas.gender.id",
          "product.schemas.size_system.id",
          "product.schemas.weight.id",
          "product.schemas.box_size.id",
          "product.schemas.line_name.id"
        ], //page
        3: ["product.category_id"], //bundle
      },
      dynamicBlocks: {
        1: ["products"], //list
        2: ["similar_products"], //page
        3: ["similar_products"], //bundle
      },
    };
  },
  computed: {
    mediaServer() {
      return process.env.VUE_APP_MEDIA_URL;
    },
    activePossibleVariables() {
      if(this.activePage?.page_type !== null){
        return this.possibleVariables[this.activePage?.page_type] ?? [];
      }
      return [];
    },
    activeDynamicBlocks() {
      if(this.activePage?.page_type !== null){
        return this.dynamicBlocks[this.activePage?.page_type] ?? [];
      }
      return [];
    },
  },
  mounted() {
    //eslint-disable-next-line no-undef
    this.loaded.bar = new ldBar(this.$refs.loadBar);

    http.fetch(`/themes`).then((data) => {
      this.template = data.data[0].id;
      this.templates = data.data;

      http.fetch(`/page/${this.activePage.id}`).then((data) => {
        this.activePage = data.data;
        this.language = data.data.language_code;

        this.pageJson = data.data.json;

        (async () => {
          this.loaded.var = 10;
          this.loaded.bar.set(this.loaded.var);

          await this.loadBlocks();
          await this.initTwig();
          await this.loadJson();
        })();

        this.generatedCssLink = "https://vivienvance.com/css/app.css";
        this.activePage.styleFormat = "sass"; // TODO
        if (this.activePage.styleFormat === "sass") {
          this.generateSass();
        } else {
          this.generatedCss = null;
          this.loaded.style = true;
          this.loaded.var += 20;
          this.loaded.bar.set(this.loaded.var);
          this.setScript();
        }
      });

    });

    if (this.languages.length < 1) {
      http.fetch("/languages").then((data) => {
        this.languages = data;
        if (this.language === null || this.language?.length !== 2) {
          this.language = data[0].code;
        }
      });
    }

    window.addEventListener("resize", this.getDimensions);
  },
  unmounted() {
    window.removeEventListener("resize", this.getDimensions);
  },
  methods: {
    addNestedItem(parentKey, parentIndex, subBlock) {
      if(this.selectedBlock.value.settings[parentKey][parentIndex] === undefined){
        this.selectedBlock.value.settings[parentKey][parentIndex] = {};
      }
      if(this.selectedBlock.value.settings[parentKey][parentIndex][subBlock.key] === undefined){
        this.selectedBlock.value.settings[parentKey][parentIndex][subBlock.key] = [];
      }
      this.selectedBlock.value.settings[parentKey][parentIndex][subBlock.key].push({});
    },

    deleteNestedItem(parentKey, parentIndex, subKey, subIndex) {
      this.selectedBlock.value.settings[parentKey][parentIndex][subKey].splice(subIndex, 1);
    },
    async loadProducts(section) {
      let searchParams = {
        status: [1],
      };

      if (typeof section.settings.featured_category === "object") {
        searchParams["category_id"] = section.settings.featured_category;
      }
      if (
        typeof section.settings.tags === "object" &&
        section.settings.tags !== null &&
        section.settings.tags?.length > 0
      ) {
        searchParams["tags"] = section.settings.tags;
      }
      if (
        typeof section.settings.characteristics === "object" &&
        section.settings.characteristics !== null
      ) {
        searchParams["characteristics"] = section.settings.characteristics;
      }
      if (typeof section.settings.featured_products === "object") {
        searchParams["variant_id"] = section.settings.featured_products;
      }
      if (typeof section.settings.featured_bundles === "object") {
        searchParams["bundle_id"] = section.settings.featured_bundles;
      }

      return await http.fetch("/products/products/search", searchParams);
    },
    toggleStyle(style) {
      const key = style.key;
      const current = this.selectedBlock.value.settings[key];
      if (current === style.class) {
        delete this.selectedBlock.value.settings[key];
      } else {
        this.selectedBlock.value.settings[key] = style.class;
      }
      this.changeBlockStyle();
    },
    async changeBlockStyle() {
      this.selectedBlock.value.settings.margin = this.serializeMarginObject(
        this.values
      );
      const section = this.selectedBlock.value;
      const context = {
        settings: section.settings || {},
        section,
      };
      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    async editContentChange(settings) {
      Object.assign(this.editContentSettings, settings);

      const section = this.selectedBlock.value;

      if (
        this.selectedBlock.type?.type === "featured-products" &&
        (section.settings.featured_products === undefined ||
          section.settings.featured_bundles === undefined)
      ) {
        section.products = await this.loadProducts(section);
      }

      const context = {
        settings: section.settings || {},
        section,
      };

      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    parseMarginString(str, config) {
      const result = {
        top: "",
        bottom: "",
        left: "",
        right: "",
      };
      if (typeof str !== "string" || !str.trim()) return result;

      const parts = str.trim().split(/\s+/);
      for (const part of parts) {
        for (const side of ["top", "bottom", "left", "right"]) {
          if (part in config[side].classes) {
            result[side] = part;
            break;
          }
        }
      }
      return result;
    },
    serializeMarginObject(obj) {
      const parts = [];
      if (obj.top) parts.push(obj.top);
      if (obj.right) parts.push(obj.right);
      if (obj.bottom) parts.push(obj.bottom);
      if (obj.left) parts.push(obj.left);
      return parts.join(" ");
    },
    showEditBlock(block) {
      this.selectedBlock = block;
      if (typeof this.selectedBlock?.value.settings.margin === "string") {
        this.values = this.parseMarginString(
          this.selectedBlock?.value.settings["margin"],
          this.margin
        );
      }
    },
    findRealMove(oldOrder, newOrder) {
      if (!Array.isArray(oldOrder) || !Array.isArray(newOrder)) {
        return null;
      }

      let moved = null;
      let maxDelta = 0;

      for (const key of oldOrder) {
        const from = oldOrder.indexOf(key);
        const to = newOrder.indexOf(key);

        if (to === -1) continue;

        const delta = Math.abs(from - to);
        if (delta > maxDelta) {
          maxDelta = delta;
          moved = { key, from, to };
        }
      }

      return moved;
    },
    moveEvent() {
      const oldOrder = [...this.pageJson.order];

      const newOrder = this.blocks.map(block => block.key);
      this.pageJson.order = newOrder;


      const realMove = this.findRealMove(oldOrder,newOrder);
      let doc = this.$refs.preview.contentWindow.document;
      const el = doc.querySelector(`[data-block_id="${realMove.key}"]`);
      if (el == undefined || el == null) {
        this.loadJson();
      }else{
        try {
          const parent = el.parentNode;
          parent.removeChild(el);
          const target = doc.querySelector(`[data-block_id="${newOrder[realMove.to + 1 ?? "----"]}"]`);
          if (target) {
            parent.insertBefore(el, target);
          } else {
            parent.appendChild(el);
          }
        } catch (e) {
          this.loadJson();
        }
      }
    },
    reloadBlock(name, context, id) {
      let doc = this.$refs.preview.contentWindow.document;

      const el = doc.querySelector(`[data-block_id="${id}"]`);
      if (el == undefined || el == null) {
        return false;
      }

      Twig.twig({
        name: `layout/${name}.twig`,
        method: "file",
        async: false,
        load(template) {
          let html = template.render(context);

          const wrapper = document.createElement("div");
          wrapper.innerHTML = html.trim();

          const firstElement = wrapper.firstElementChild;
          if (firstElement) {
            firstElement.setAttribute("data-block_id", id);
            el.replaceWith(firstElement);
          }
        },
        error(err) {
          console.error("Failed to reload block:", err);
        },
      });

      return true;
    },
    async loadSection(name, context, id = null) {
      const _this = this;
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          Twig.twig({
            name: `layout/${name}.twig`,
            method: "file",
            async: false,
            load(template) {
              let html = template.render(context);
              const wrapper = document.createElement("div");
              wrapper.innerHTML = html.trim();

              const elements = Array.from(wrapper.childNodes).filter(
                (node) =>
                  node.nodeType === Node.ELEMENT_NODE ||
                  (node.nodeType === Node.TEXT_NODE &&
                    node.textContent.trim() !== "")
              );

              const firstElement = wrapper.firstElementChild;
              if (elements.length === 1 && firstElement) {
                firstElement.setAttribute("data-block_id", id);
              }

              html = wrapper.innerHTML;
              _this.contentForLayout += html;
              clearInterval(interval);
              if (_this.loaded.var < 90) {
                _this.loaded.var += 5;
                _this.loaded.bar.set(_this.loaded.var);
              }
              resolve();
            },
            error(err) {
              console.error("Failed to load template:", err);
            },
          });
        }, 1000);
      });
    },
    async loadJson() {
      if (this.jsonUnderLoad || this.template === undefined || this.possibleBlocks.length < 1) {
        return;
      }
      this.jsonUnderLoad = true;
      this.blocks = [];
      this.contentForLayout = "";
      if (this.pageJson === null) {
        this.pageJson = {
          "sections": {},
          "order": []
        };
      }
      let loads = [];

      for (const key of this.pageJson.order) {
        const section = this.pageJson.sections[key];
        console.log(key);
        const context = {
          settings: section.settings || {},
          section,
        };

        if (this.possibleBlocks[section.type]?.type === "featured-products") {
          section.products = await this.loadProducts(section);
        }


        if (this.blocks.findIndex((e) => e.key === key) < 0) {
          this.blocks.push({
            key: key,
            typeName: section.type,
            type: this.possibleBlocks[section.type],
            value: section || {},
          });
        }
        loads.push(this.loadSection("sections/" + section.type, context, key));
      }
      await Promise.all(loads);
      this.loaded.content = true;
      this.loadTwig();
      this.jsonUnderLoad = false;
    },
    async initTwig() {
      const _this = this;

      Twig.extend(function (Twig) {
        Twig.Templates.registerLoader(
          "file",
          function (location, params, callback, errorCallback) {
            _this.loaded.var += 5;
            _this.loaded.bar.set(_this.loaded.var);
            if (
              location.startsWith("layout/sections")
            ) {
              location = location.replace("layout/", "");
            } else if (location.startsWith("sections/snippets")) {
              location = location.replace("sections/snippets", "snippets");
            }

            if(_this.files[location] !== undefined){
              params.data = _this.files[location];
              const template = new Twig.Template(params);
              if (callback !== undefined) {
                callback(template);
              }
              return template;
            }

            http
                .fetch("/themes/template/" + _this.template + "/file", {
                  file: location,
                },false,null,false)
                .then((data) => {
                  _this.files[location] = data;
                  params.data = data;
                  const template = new Twig.Template(params);
                  if (callback !== undefined) {
                    callback(template);
                  }
                  return template;
                })
                .catch(errorCallback);
          }
        );
      });


      Twig.extendFunction("include_html", function (value) {
        return value;
      });

      Twig.extendFunction("linklists", function () {
        return [];
      });


      Twig.extendFilter("asset_url", function (value) {
        return `/templates/${_this.template}/assets/${value}`;
      });

      Twig.extendFilter("t", function (value) {
        if (typeof value === "object" && Object.keys(value)?.length > 0) {
          return value[_this.language] ?? value[Object.keys(value)[0]];
        }
        return value;
      });
      Twig.extendFilter("trans", function (value) {
        return value;
      });

      Twig.extendFilter("image_url", function (value, args) {
        const params = args?.[0] || {};
        const query = new URLSearchParams(params).toString();

        let url = "";

        if (typeof value === "string") {
          url = `https://media-01.prodyflow.com/storage/vivienvance/images/${value}`;
        } else if (typeof value === "object" && value?.src) {
          url = value.src;
        }

        return query ? `${url}?${query}` : url;
      });

      Twig.extendFilter("image_srcset", function (value) {
        if (typeof value === "string") {
          return `https://media-01.prodyflow.com/storage/vivienvance/images/${value}`;
        }

        if (typeof value === "object") {
          if (value?.sizes) {
            return Object.entries(value.sizes)
              .map(([width, url]) => `${url} ${width}w`)
              .join(", ");
          }

          if (value?.src) {
            return value.src;
          }
        }

        return "";
      });

      Twig.extendFilter("video_srcset", function (value) {
        if (typeof value === "object") {
          return JSON.stringify([
            {
              src: value.path + "/360p.webm",
              type: "video/webm",
              quality: 360,
            },
            { src: value.path + "/360p.mp4", type: "video/mp4", quality: 360 },
            {
              src: value.path + "/480p.webm",
              type: "video/webm",
              quality: 480,
            },
            { src: value.path + "/480p.mp4", type: "video/mp4", quality: 480 },
            {
              src: value.path + "/720p.webm",
              type: "video/webm",
              quality: 720,
            },
            { src: value.path + "/720p.mp4", type: "video/mp4", quality: 720 },
          ]);
        }
        return "";
      });
      Twig.extendFilter("video_poster", function (value) {
        if (typeof value === "object") {
          return value.path + "/frame1.jpg";
        }
        return "";
      });

      Twig.extendFilter("money", function (value) {
        if (typeof value === "number") {
          return value.toLocaleString("hu-HU", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
        } else if (typeof value === "object") {
          return value.amount.toLocaleString("hu-HU", {
            style: "currency",
            currency: value.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: value.currency === "HUF" ? 0 : 2,
          });
        }

        return "123 €";
      });
    },
    loadTwig() {
      if (this.interval !== null && this.interval !== undefined) {
        return;
      }
      const _this = this;

      let doc = this.$refs.preview.contentWindow.document;

      const context = {
        now: Math.floor(Date.now() / 1000),
        page_title: "My Page",
        template: "index", // vagy pl. "product", "collection", "cart", stb.

        shop: {
          name: "Example Shop",
          email: "info@example.com",
          domain: "example.prodyflow.com",
          url: "https://example.prodyflow.com",
          currency: "HUF",
          money_format: "{{ amount_no_decimals_with_space_separator }} Ft",
          description: "Példa shop leírás",
        },

        settings: {
          logo: "https://vivienvance.com/images/logo.png",
          favicon: "/assets/favicon.png",
          type_header_font: { system: false },
          type_body_font: { system: false },
          color_primary: "#f03e3e",
          show_cart_icon: true,
          show_search: true,
          store_locale: "hu",
        },

        cart: {
          item_count: 2,
          total_price: 32970,
          items: [
            {
              title: "MARE BIKINI TOP",
              quantity: 1,
              price: 10990,
              line_price: 10990,
              image: "https://cdn.shopify.com/s/files/1/xxx/products/1.jpg",
              url: "/products/mare-bikini-top",
              variant_title: "L",
            },
            {
              title: "ZAFFIRO PAMUT TANGA ALSÓ",
              quantity: 2,
              price: 10990,
              line_price: 21980,
              image: "https://cdn.shopify.com/s/files/1/xxx/products/2.jpg",
              url: "/products/zaffiro-tanga",
              variant_title: "L",
            },
          ],
        },

        collection: {
          title: "Bikini Kollekció",
          description: "Nyári bikini választék",
          handle: "bikini-kollekcio",
          products: [
            {
              title: "MARE BIKINI TOP",
              price: 10990,
              featured_image: "/assets/bikini.jpg",
              vendor: "Vivien Vance",
              url: "/products/mare-bikini-top",
            },
            {
              title: "ZAFFIRO PAMUT TANGA ALSÓ",
              price: 10990,
              featured_image: "/assets/tanga.jpg",
              vendor: "Vivien Vance",
              url: "/products/zaffiro-tanga",
            },
          ],
        },

        product: {
          title: "MARE BIKINI TOP",
          price: 10990,
          compare_at_price: 13990,
          description: "<p>Részletes leírás itt...</p>",
          handle: "mare-bikini-top",
          images: ["/assets/bikini1.jpg", "/assets/bikini2.jpg"],
          options: ["Méret", "Szín"],
          variants: [
            {
              id: 1,
              title: "L / Piros",
              available: true,
              price: 10990,
              image: "/assets/bikini1.jpg",
            },
          ],
          url: "/products/mare-bikini-top",
        },

        customer: {
          first_name: "Anna",
          last_name: "Kiss",
          email: "anna@example.com",
          logged_in: true,
        },

        current_tags: ["summer", "sale"],
        current_page: 2,
        request: { locale: { iso_code: "hu" } },
        routes: {
          cart_url: "/cart",
          account_url: "/account",
          search_url: "/search",
        },

        content_for_header: "",
        content_for_layout: _this.contentForLayout,

        section: {
          id: "featured-collection",
          settings: {
            title: "Kiemelt termékek",
            featured_collection: {
              title: "Nyári Bikini Kollekció",
              products: [], // opcionálisan töltsd
            },
            number_of_products_to_show: 4,
          },
        },

        // Például navigation menühöz
        linklists: {
          main_menu: [
            { title: "Home", url: "/", active: false },
            { title: "Bikini", url: "/bikini", active: true },
            { title: "Fehérnemű", url: "/lingerie", active: false },
            { title: "Fitness", url: "/fitness", active: false },
          ],
        },
      };

      this.interval = setInterval(() => {
        Twig.twig({
          name: "layout/theme.twig",
          method: "file",
          async: false,
          load(template) {
            doc.open();
            doc.write(template.render(context));
            doc.close();
            clearInterval(_this.interval);
            _this.interval = null;
            if (_this.loaded.var < 50) {
              _this.loaded.var = 50;
              _this.loaded.bar.set(_this.loaded.var);
            }
            _this.loaded.page = true;
            _this.setCss();
            _this.setScript();
          },
          error(err) {
            console.error("Failed to load template:", err);
          },
        });
      }, 200);

      this.calcRate();
    },
    async loadBlocks() {
      const blocks = await http
          .fetch("/themes/template/" + this.template + "/file", {
            file: "blocks.json",
          });
      this.possibleBlocks = blocks;
    },
    save() {
      this.loaded.var = 10;
      this.loaded.bar.set(this.loaded.var);
      this.loaded.script = false;

      this.loaded.var = 40;
      this.loaded.bar.set(this.loaded.var);

      let pageData = this.pageJson;

      for (const key in pageData.sections) {
        if (typeof pageData.sections[key].products === "object") {
          delete pageData.sections[key].products;
        }
      }

      http
        .fetch(
          `/page/${this.activePage.id}`,
          {
            json: JSON.stringify(pageData),
          },
          true,
          "PUT"
        )
        .then(() => {
          this.loaded.var = 90;
          this.loaded.bar.set(this.loaded.var);
          this.loaded.script = true;
        });
    },
    getDimensions() {
      this.calcRate();
    },
    generateID(length = 8) {
      return Math.random().toString(36).substr(2, length);
    },
    async addBlock(type) {
      let key = type;
      if (this.pageJson.sections[type] !== undefined) {
        key += this.generateID();
      }
      this.pageJson.sections[key] = {
        type: type,
        settings: {},
      };

      if(this.possibleBlocks[type]?.dynamic_data !== undefined && this.possibleBlocks[type]?.dynamic_data !== null){
        this.pageJson.sections[key].dynamic_data = this.possibleBlocks[type]?.dynamic_data;
      }

      this.pageJson.order.push(key);

      let section = this.pageJson.sections[key];

      if (this.blocks.findIndex((e) => e.key === key) < 0) {
        this.blocks.push({
          key: key,
          typeName: type,
          type: this.possibleBlocks[section.type],
          value: section || {},
        });
        const context = {
          settings: section.settings || {},
          section,
        };
        await this.loadSection("sections/" + section.type, context, key);
      }
     // this.loadJson();
      this.loadTwig();
    },
    calcRate() {
      if (!this.$refs.outsideContainer || !this.$refs.container || !this.$refs.preview) {
        return;
      }
      if (this.size === "wide") {
        this.zoom = 0.9;
      } else if (this.size === "phone") {
        this.zoom = 1.2;
      } else {
        this.zoom = 1;
      }

      const preview = this.$refs.preview;
      if (preview.contentWindow) {
        if (this.size === "wide") {
          const doc = preview.contentWindow.document;
          const style = doc.querySelector(".css-phone");
          if (style) style.remove();
        } else {
          let doc = preview.contentWindow.document;

          if (!doc.querySelector(".css-phone")) {
            let style = document.createElement("STYLE");
            style.classList.add("css-phone");
            style.innerHTML = "body::-webkit-scrollbar {display: none;}";
            doc.head.appendChild(style);
          }
        }
      }

      let max = this.$refs.outsideContainer.offsetWidth - 20;
      let actual = this.$refs.container.offsetWidth;
      if (max < actual) {
        this.rate = max / actual;
        if (this.size === "wide") {
          this.height =
            this.$refs.outsideContainer.offsetHeight *
            (1 / this.rate) *
            (1 / this.zoom);
        } else {
          this.height = undefined;
        }
      } else {
        this.rate = 1;
      }
    },
    async deleteBlock() {
      let i = this.pageJson.order.findIndex(
        (e) => e === this.selectedBlock.key
      );
      delete this.pageJson.sections[this.selectedBlock.key];
      this.pageJson.order.splice(i, 1);
      this.blocks.splice(i, 1);

      let doc = this.$refs.preview.contentWindow.document;

      const el = doc.querySelector(
        `[data-block_id="${this.selectedBlock.key}"]`
      );
      if (el !== undefined && el !== null) {
        el.parentNode.removeChild(el);
      } else {
        await this.loadBlocks();
        await this.loadJson();
      }

      this.selectedBlock = null;
    },
    async deleteSubBlock(key, i) {
      this.selectedBlock.value.settings[key].splice(i, 1);

      const section = this.selectedBlock.value;

      if (
        this.selectedBlock.type?.type === "featured-products" &&
        (section.settings.featured_products === undefined ||
          section.settings.featured_bundles === undefined)
      ) {
        section.products = await this.loadProducts(section);
      }

      const context = {
        settings: section.settings || {},
        section,
      };

      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    getSectionTitle(content, block) {
      const main = block.blocks.find((e) => e.main);
      if (!main) return "";

      const value = content?.[main.key];
      if (!value || typeof value !== "object") return "";

      const lang = this.language;
      const fallback = this.activePage?.default_language;

      const firstKey = Object.keys(value)[0];

      if (
        main.translate && Array.isArray(value)
          ? value.length > 0
          : Object.keys(value).length > 0
      ) {
        return (
          (lang && value[lang]) ??
          (fallback && value[fallback]) ??
          (firstKey ? value[firstKey] : "") ??
          ""
        );
      }

      return value ?? "";
    },
    editContent(block, settings) {
      this.editContentBlock = block;
      this.editContentSettings = settings;
      this.editContentShow = true;
    },
    addNewBlockItem(block) {
      if (this.selectedBlock.value.settings[block.key] === undefined) {
        this.selectedBlock.value.settings[block.key] = [];
      }
      this.selectedBlock.value.settings[block.key].push({});
    },
    setScript() {
      if (!this.loaded.page || !this.loaded.style) {
        return;
      }
      const _this = this;
      if (!_this.loaded.script) {
        let doc = _this.$refs.preview.contentWindow.document;

        const script2 = document.createElement("script");
        script2.src = "/readyState.js";
        script2.onload = function () {
          const script = document.createElement("script");
          script.onload = function () {
            _this.loaded.script = true;
          };
          script.src = "https://vivienvance.com/js/app.js";
          doc.head.appendChild(script);
        };
        doc.head.appendChild(script2);
      }
    },
    setCss() {
      if (!this.loaded.page || !this.loaded.content || this.generatedCss?.length < 1) {
        return;
      }
      this.loaded.var = 98;
      this.loaded.bar.set(this.loaded.var);

      let doc = this.$refs.preview.contentWindow.document;

      let div = document.createElement("STYLE");
      div.classList.add("generated-css-main");
      div.innerHTML = this.generatedCss;
      doc.head.appendChild(div);

      let div3 = document.createElement("LINK");
      div3.classList.add("generated-css-link");
      div3.rel = "stylesheet";
      div3.href = this.generatedCssLink;
      doc.head.appendChild(div3);

      this.loaded.style = true;
      this.loaded.var = 99;
      this.loaded.bar.set(this.loaded.var);

      this.setScript();
    },
    changeGlobalSettingsModal() {
      this.getGlobalSettings().then((variables) => {
        this.globalVariables = variables;
        this.globalShettingsShow = true;
      });
    },
    addImportCss(src) {
      let doc = this.$refs.preview.contentWindow.document;
      const style = document.createElement("style");
      style.innerText = `@import url('${src}');`;
      doc.head.appendChild(style);
    },
    getGlobalSettings() {
      return new Promise((resolve) => {
        http
            .fetch("/themes/template/" + this.template + "/file", {
              file: `css/variables.scss`
            },false,null,false)
            .then((scss) => {
              let colors = scss.matchAll(
                  /\$([\S]{0,100})[\s]{0,4}:[\s]{0,4}(#[0-9ABCDEF]{3,6}|rgb[a]{0,1}\([0-9,.;% ]{3,30}\))/gim
              );
              let everyVariables = scss.matchAll(
                  /\$([\S]{0,100})[\s]{0,4}:[\s]{0,4}([^\n;]{0,100})[\s;]{0,4}(\/\*[\s]{0,4}([^/]{0,100})[\s]{0,4}\*\/){0,1}/gim
              );

              let variables = {};
              let types = ["font-family", "font-size", "line-height"];

              for (const match of colors) {
                variables[match[1]] = { value: match[2], type: "color" };
              }
              for (const match of everyVariables) {
                if (variables[match[1]] === undefined) {
                  let type = undefined;
                  if (match[4] !== undefined && types.indexOf(match[4]) > -1) {
                    type = match[4];
                    match[2] = match[2].replace("px", "");
                  }
                  variables[match[1]] = { value: match[2], type: type };
                }
              }
              resolve(variables);
            });
      });
    },
    changeStyle(key, val) {
      let doc =
        this.$refs.preview.contentWindow.document.querySelector(":root");
      doc.style.setProperty("--" + key, val);
    },
    generateSass() {
      this.generatedCss = "";
      const Sass = require("../sass-js/sass.js");

      Sass.setWorkerUrl("/sass-js/sass.worker.js");

      const sass = new Sass();
      const _this = this;

      const value = localStorage.getItem("sass");
      if (value !== null) {
        _this.generatedCss = value;
        _this.setCss();
        return;
      }
      sass.importer(function (request, done) {
        if (_this.loaded.var > 98) {
          _this.loaded.var = 99;
        } else if (_this.loaded.var > 75) {
          _this.loaded.var += 1;
        } else {
          _this.loaded.var += 5;
        }
        _this.loaded.bar.set(_this.loaded.var);
        if (request.path) {
          done();
        } else if (request.current === "variables") {
          _this.getGlobalSettings().then((variables) => {
            let rootCss = ":root {\n";
            let varCss = "";
            for (const key in variables) {
              rootCss += `--${key}: ${variables[key].value};\n`;
              varCss += `$${key}: var(--${key});\n`;
            }
            rootCss += "}";
            done({ content: rootCss + " " + varCss });
          });
        } else {
          let name = request.current + ".scss";
          if (
            request.current.indexOf(".scss") > 0 ||
            request.current.indexOf(".css") > 0
          ) {
            name = request.current;
          }

          http
              .fetch("/themes/template/" + _this.template + "/file", {
                file: `css/${name}`
              },false,null,false)
              .then((scss) => {
                done({ content: scss });
            }).catch(() => {
              http
                  .fetch("/themes/template/" + _this.template + "/file", {
                    file: `css/${name}.css`
                  },false,null,false)
                  .then((scss) => {
                    done({ content: scss });
                  });
            });
        }
      });

      http
          .fetch("/themes/template/" + _this.template + "/file", {
            file: `css/app.scss`
          },false,null,false)
          .then((scss) => {
          sass.compile(scss, function (result) {
            _this.generatedCss = result.text;
            localStorage.setItem("sass", result.text);
            _this.setCss();
          });
        })
        .catch((e) => {
          console.log("CSS ERROR:");
          console.log(e);
          if (_this.loaded.var < 50) {
            _this.loaded.style = true;
            _this.loaded.var = 50;
          }
        });
    },
  },
};
</script>
